import { useTranslation } from 'react-i18next'

const PALIERS = [
  { seuil: 0, nom: 'bronze', emoji: '🥉', couleur: '#CD7F32', bgFrom: '#fef3c7', bgTo: '#fde68a', textColor: '#92400e', labelColor: '#b45309' },
  { seuil: 25, nom: 'argent', emoji: '🥈', couleur: '#C0C0C0', bgFrom: '#f1f5f9', bgTo: '#e2e8f0', textColor: '#334155', labelColor: '#64748b' },
  { seuil: 50, nom: 'or', emoji: '🥇', couleur: '#FFD700', bgFrom: '#fef3c7', bgTo: '#fde68a', textColor: '#92400e', labelColor: '#b45309' },
  { seuil: 100, nom: 'diamant', emoji: '💎', couleur: '#60a5fa', bgFrom: '#eff6ff', bgTo: '#dbeafe', textColor: '#1e40af', labelColor: '#3b82f6' },
  { seuil: 200, nom: 'maitre', emoji: '👑', couleur: '#a855f7', bgFrom: '#faf5ff', bgTo: '#f3e8ff', textColor: '#6b21a8', labelColor: '#9333ea' },
] as const

type Palier = (typeof PALIERS)[number]

function getPalier(count: number) {
  let current: Palier = PALIERS[0]
  for (const p of PALIERS) {
    if (count >= p.seuil) current = p
  }
  const idx = PALIERS.indexOf(current)
  const next = idx < PALIERS.length - 1 ? PALIERS[idx + 1] : null
  return { current, next }
}

interface RewardBannerProps {
  totalTachesTerminees: number
}

export function RewardBanner({ totalTachesTerminees }: RewardBannerProps) {
  const { t } = useTranslation()
  const { current, next } = getPalier(totalTachesTerminees)

  // Progression vers le prochain palier (0-100%)
  const progressPercent = next
    ? Math.min(100, ((totalTachesTerminees - current.seuil) / (next.seuil - current.seuil)) * 100)
    : 100

  // Circumference for SVG ring (r=20, C=2*PI*20≈125.66)
  const circumference = 2 * Math.PI * 20
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference

  return (
    <div
      className="flex items-center gap-3 rounded-xl px-3.5 py-2.5"
      style={{
        background: `linear-gradient(135deg, ${current.bgFrom} 0%, ${current.bgTo} 100%)`,
      }}
    >
      {/* Ring de progression */}
      <div className="relative h-12 w-12 flex-shrink-0">
        <svg width="48" height="48" viewBox="0 0 48 48" className="-rotate-90">
          <circle cx="24" cy="24" r="20" fill="none" stroke="#e5e7eb" strokeWidth="4" />
          <circle
            cx="24"
            cy="24"
            r="20"
            fill="none"
            stroke={current.couleur}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-lg">
          {current.emoji}
        </span>
      </div>

      {/* Infos */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-extrabold" style={{ color: current.textColor }}>
            {totalTachesTerminees}
          </span>
          <span className="text-xs" style={{ color: current.labelColor }}>
            {t('mobile.dashboard.reward.tasks', 'tâches')}
          </span>
        </div>
        {next ? (
          <div className="text-[11px]" style={{ color: current.labelColor }}>
            {t('mobile.dashboard.reward.nextTier', 'Prochain palier : {{tier}} ({{count}})', {
              tier: t(`mobile.dashboard.reward.tier.${next.nom}`),
              count: next.seuil,
            })}
          </div>
        ) : (
          <div className="text-[11px]" style={{ color: current.labelColor }}>
            {t('mobile.dashboard.reward.maxTier', 'Palier maximum atteint !')}
          </div>
        )}
      </div>

      {/* Badge palier */}
      <span
        className="flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold text-white uppercase"
        style={{ backgroundColor: current.couleur }}
      >
        {t(`mobile.dashboard.reward.tier.${current.nom}`)}
      </span>
    </div>
  )
}
